import { DateCalendar } from '@mui/x-date-pickers';
import { Box, Collapse, Divider, Grid, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FC } from 'react';

import { useDateTimeSelection } from './use-date-time-selection';
import { IOptions } from './date-picker.types';
import TimeSelector from './time-selector';
import { addMonths, startOfMonth } from 'date-fns';
interface IProps {
	minDate: Date;
	maxDate: Date;
	value?: Date;
	time?: boolean;
	minute?: string;
	hour?: string;
	error?: boolean;
	errorMessage?: string;
	hideLabel?: boolean;
	hideDates?: number[];
	setHour?: (values: string) => void;
	setMinute?: (values: string) => void;
	onChange: (newDate: Date | null | undefined) => void;
}

interface ISingleCalendarProps {
	value?: Date;
	onChange: (newDate: Date | null | undefined) => void;
	minDate: Date;
	maxDate: Date;
	disableDates: (date: Date) => boolean;
	hideLabel?: boolean;
	error?: boolean;
	errorMessage?: string;
	time?: boolean;
	hour?: string;
	minute?: string;
	minutesOptions?: IOptions[];
	hoursOptions?: IOptions[];
	setMinute?: (value: string) => void;
	handleHourChange: (value: string) => void;
	theme: Theme;
}

const SingleCalendar: FC<ISingleCalendarProps> = ({
	value,
	onChange,
	minDate,
	maxDate,
	disableDates,
	hideLabel,
	error,
	errorMessage,
	time,
	hour,
	minute,
	minutesOptions,
	hoursOptions,
	setMinute,
	handleHourChange,
	theme,
}) => (
	<Box
		sx={{
			borderRadius: '8px',
			padding: '0.5rem',
			paddingBottom: '1rem',
			maxWidth: 'unset',
			backgroundColor: theme.palette.grey[100],
			'& .MuiPickersArrowSwitcher-button': {
				display: hideLabel ? 'none' : 'block',
			},
			'& .MuiPickersCalendarHeader-label': {
				display: hideLabel ? 'none' : 'block',
			},
		}}
	>
		<DateCalendar
			value={value}
			onChange={(newDate) => onChange(newDate)}
			views={['day']}
			view='day'
			disableHighlightToday
			minDate={minDate}
			maxDate={maxDate}
			shouldDisableDate={disableDates}
		/>
		{time && (
			<TimeSelector
				error={error}
				minute={minute}
				minutesOptions={minutesOptions}
				setMinute={setMinute}
				hoursOptions={hoursOptions}
				setHour={handleHourChange}
				hour={hour}
			/>
		)}
		<Collapse in={error}>
			<Box sx={{ paddingTop: '1rem' }}>
				<Typography sx={{ color: 'error.main' }}>{errorMessage}</Typography>
			</Box>
		</Collapse>
	</Box>
);

interface IDualCalendarProps extends ISingleCalendarProps {
	monthIndex: number | null;
	nextMonth: Date;
}

const DualCalendar: FC<IDualCalendarProps> = ({
	value,
	onChange,
	minDate,
	maxDate,
	disableDates,
	hideLabel,
	error,
	errorMessage,
	time,
	hour,
	minute,
	minutesOptions,
	hoursOptions,
	setMinute,
	handleHourChange,
	theme,
	monthIndex,
	nextMonth,
}) => (
	<Grid
		container
		sx={{
			borderRadius: '8px',
			padding: '1rem',
			maxWidth: '800px',
			backgroundColor: theme.palette.grey[100],
			margin: 'auto',
			flexWrap: 'wrap',
			maxHeight: '500px',
			[theme.breakpoints.down('md')]: {
				padding: '0.5rem',
				paddingBottom: '1rem',
				maxWidth: 'unset',
			},
		}}
	>
		<Grid
			size={{ sm: 6, md: 6 }}
			sx={{
				'& .MuiPickersArrowSwitcher-button': {
					display: 'none',
				},
				'& .MuiPickersCalendarHeader-label': {
					display: hideLabel ? 'none' : 'block',
				},
				borderRadius: '8px',
				maxWidth: '800px',
				backgroundColor: theme.palette.grey[100],
				margin: 'auto',
				[theme.breakpoints.down('md')]: {
					padding: '0.5rem',
					paddingBottom: '1rem',
					maxWidth: 'unset',
				},
			}}
		>
			<Box sx={{ display: 'flex', flexDirection: 'row' }}>
				<DateCalendar
					value={monthIndex === 0 ? value : null}
					views={['day']}
					view='day'
					onChange={(newDate) => onChange(newDate)}
					disableHighlightToday
					minDate={minDate}
					maxDate={maxDate}
					shouldDisableDate={disableDates}
				/>
				<Divider orientation='vertical' flexItem sx={{ mr: '-1px' }} />
			</Box>
		</Grid>
		<Grid
			size={{ sm: 6, md: 6 }}
			sx={{
				'& .MuiPickersArrowSwitcher-button': {
					display: 'none',
				},
				'& .MuiPickersCalendarHeader-label': {
					display: hideLabel ? 'none' : 'block',
				},
			}}
		>
			<DateCalendar
				value={monthIndex === 1 ? value : undefined}
				onChange={(newDate) => {
					onChange(newDate);
				}}
				views={['day']}
				view='day'
				disableHighlightToday
				maxDate={maxDate}
				minDate={nextMonth}
				shouldDisableDate={disableDates}
			/>
		</Grid>

		{time && (
			<TimeSelector
				error={error}
				minute={minute}
				minutesOptions={minutesOptions}
				setMinute={setMinute}
				hoursOptions={hoursOptions}
				setHour={handleHourChange}
				hour={hour}
			/>
		)}
		<Collapse in={error}>
			<Box sx={{ paddingTop: '1rem' }}>
				<Typography sx={{ color: 'error.main' }}>{errorMessage}</Typography>
			</Box>
		</Collapse>
	</Grid>
);

const adjustValueForMinDate = (value: Date | undefined, minDate: Date): void => {
	if (value && value < minDate) {
		value.setDate(minDate.getDate());
	}
};

const getFirstDayOfNextMonth = (date: Date): Date => {
	const nextMonth = addMonths(date, 1);
	const firstDayOfNextMonth = startOfMonth(nextMonth);
	return firstDayOfNextMonth;
};

const getMonthIndex = (date: Date, startDate: Date, endDate: Date) => {
	const startYear = startDate.getFullYear();
	const startMonth = startDate.getMonth();
	const endMonth = endDate.getMonth();

	let months = endMonth - startMonth;

	if (months < 0) {
		months += 12;
	}

	let monthIndex = 0;

	if (date >= startDate && date <= endDate) {
		monthIndex = (date.getFullYear() - startYear) * 12 + date.getMonth() - startMonth;
	}

	return monthIndex;
};

const calculateDurationInMonths = (startDate: Date, endDate: Date) => {
	const startYear = startDate.getFullYear();
	const endYear = endDate.getFullYear();
	const startMonth = startDate.getMonth();
	const endMonth = endDate.getMonth();

	let months = endMonth - startMonth;
	let years = endYear - startYear;

	if (months < 0) {
		years -= 1;
		months += 12;
	}

	return years * 12 + months;
};

const DayPicker: FC<IProps> = ({
	minDate,
	maxDate,
	value,
	onChange,
	time,
	setHour,
	setMinute,
	hour,
	minute,
	hideLabel,
	error = false,
	errorMessage,
	hideDates,
}: IProps) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const months = calculateDurationInMonths(minDate, maxDate);
	const monthIndex = value ? getMonthIndex(value, minDate, maxDate) : null;
	const nextMonth = getFirstDayOfNextMonth(minDate);

	// Use the custom hook for date/time selection logic
	const { hoursOptions, minutesOptions, disableDates, handleHourChange } = useDateTimeSelection({
		value,
		hour,
		minute,
		setHour,
		setMinute,
		hideDates,
	});

	adjustValueForMinDate(value, minDate);

	// This day picker is only used to select short term dates.
	if (months > 2) return null;

	const sharedProps = {
		value,
		onChange,
		minDate,
		maxDate,
		disableDates,
		hideLabel,
		error,
		errorMessage,
		time,
		hour,
		minute,
		minutesOptions,
		hoursOptions,
		setMinute,
		handleHourChange,
		theme,
	};

	return isMobile || months === 0 ? (
		<SingleCalendar {...sharedProps} />
	) : (
		<DualCalendar {...sharedProps} monthIndex={monthIndex} nextMonth={nextMonth} />
	);
};

export default DayPicker;
